import { useEffect } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useDebouncedCallback } from 'use-debounce';
import { DatabricksSqlEditor } from '@antimatterhq/ui/components/databricksSqlEditor';
import { DateTimeRangePicker } from '@antimatterhq/ui/components/dateTimeRangePicker';
import { RefinementInput } from '@antimatterhq/ui/components/refinementInput';
import { Tabs } from '@antimatterhq/ui/components/tabs';
import { Histogram } from '@antimatterhq/ui/components/histogram';
import { QueryRunningSkeletons } from './tabContent/QueryRunningSkeletons';
import { QueryPreQueryInfo } from './tabContent/QueryPreQueryInfo';
import { QueryResultsTable } from '@/pages/explore/query/_partials/queryResultsTable/QueryResultsTable';
import {
  queryTabAtomFamily,
  updateTabAtomFamily,
  saveTabLocallyAtom,
  activeQueryTabIdAtom,
} from '@/atoms/query/queryAtoms';
import { RunQuery } from '@/components/query/run/RunQuery';
import { useRunQuery } from '@/hooks/components/query/useRunQuery';
import { useQueryHistogram } from '@/hooks/components/query/useQueryHistogram';

export function QueryTabContent() {
  const activeTabId = useAtomValue(activeQueryTabIdAtom);
  const tab = useAtomValue(queryTabAtomFamily(activeTabId ?? ''));
  const updateTab = useSetAtom(updateTabAtomFamily(activeTabId ?? ''));
  const saveTabLocally = useSetAtom(saveTabLocallyAtom);
  const {
    dataPoints,
    isPending: isHistogramPending,
    onLoadData: addTimeRange,
    loadedTimeRanges,
    onTimeRangeChange,
    onTimeRangeSelection,
  } = useQueryHistogram(activeTabId ?? '');

  const { runQuery, isPending: isRunningQuery } = useRunQuery(
    activeTabId ?? '',
  );

  const debouncedLocalSave = useDebouncedCallback(
    () => saveTabLocally(activeTabId ?? ''),
    4000,
    {
      maxWait: 8000,
    },
  );

  useEffect(() => {
    if (tab?.isLocalDirty) {
      debouncedLocalSave();
    }
    return () => {
      debouncedLocalSave.cancel();
    };
  }, [tab?.isLocalDirty, debouncedLocalSave]);

  if (!tab) return null;

  const queryIsPending = tab.status === 'running' || tab.status === 'pending';

  return (
    <Tabs.Panel className="flex flex-1 flex-col border-b border-l border-r border-grey-200 p-0 dark:border-grey-700">
      <div className="flex items-center justify-between border-b border-grey-200 p-2 dark:border-grey-700">
        <DateTimeRangePicker
          dateRange={tab.dateRange}
          onChange={(dateRange) => updateTab({ dateRange })}
          timezone={tab.timezone}
          onTimezoneChange={(timezone) => updateTab({ timezone })}
          className="max-w-[408px]"
        />
        <RunQuery running={isRunningQuery} onClick={runQuery} />
      </div>

      <div className="flex flex-col gap-2 bg-[#fafafa] dark:bg-[#252526]">
        <div className="pl-2 pr-2 pt-2 text-sm font-medium text-gray-700 dark:text-gray-300">
          Query
        </div>
        <DatabricksSqlEditor
          value={tab.sql}
          onChange={(event) => updateTab({ sql: event.target.value })}
          placeholder="Enter your SQL query here..."
          data-testid={`query-sql-editor-${activeTabId ?? ''}`}
          className="border-b border-grey-200 dark:border-grey-700"
        />
      </div>
      <RefinementInput
        refinements={tab.refinements}
        onRefinementsChange={(refinements) => updateTab({ refinements })}
        placeholder="|> Add databricks pipe syntax expressions"
        heading="Refinements"
        className="rounded-none border-b border-l-0 border-r-0 border-t-0 border-grey-200 bg-[#fafafa]"
        editableChipClassName="font-mono"
        rules={[
          {
            match: /.*/,
            theme: 'border-blue-500 bg-blue-50 dark:bg-blue-800',
            validation: () => {
              return undefined;
            },
          },
        ]}
      />

      {tab.status === 'idle' && <QueryPreQueryInfo />}

      {queryIsPending && <QueryRunningSkeletons />}

      {tab.status === 'success' && (
        <div className="sticky top-[0px] z-histogram space-y-2 bg-white dark:bg-black-100">
          <Histogram
            initialStartTime={tab.dateRange.start}
            initialEndTime={tab.dateRange.end}
            timezone={tab.timezone}
            data={dataPoints}
            isLoading={isHistogramPending || queryIsPending}
            loadedTimeRanges={loadedTimeRanges}
            onLoadData={addTimeRange}
            onTimeRangeChange={onTimeRangeChange}
            onTimeRangeSelection={onTimeRangeSelection}
          />
        </div>
      )}

      {tab.status === 'success' && <QueryResultsTable />}
    </Tabs.Panel>
  );
}
